import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Select, type Column } from "@legal-500/legal500-design-system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable/DataTable";
import {
  FirmChip,
  ProfilesFilterCell,
  ProfilesListFilters,
  RegionNodeChip,
} from "@/components/Profiles/ListFilters";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { parseRegionKind } from "@/hooks/useRegions";
import { ROUTES } from "@/routes";
import { type Article, type ArticleType, articleKeys, articlesService } from "@/api/profiles";
import styles from "../ProfilesList.module.css";

const ARTICLE_TYPE_LABELS: Record<ArticleType, string> = {
  doing_business_in: "Doing Business In",
  focus_on: "Focus On",
  interview: "Interview",
};

const ARTICLE_TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "doing_business_in", label: ARTICLE_TYPE_LABELS.doing_business_in },
  { value: "focus_on", label: ARTICLE_TYPE_LABELS.focus_on },
  { value: "interview", label: ARTICLE_TYPE_LABELS.interview },
];

function isArticleType(v: string): v is ArticleType {
  return v === "focus_on" || v === "doing_business_in" || v === "interview";
}

export function ArticlesList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteTarget, setDeleteTarget] = useState<Article | null>(null);

  const companyId = searchParams.get("companyId") ?? undefined;
  const status = searchParams.get("status") ?? undefined;
  const q = searchParams.get("q") ?? undefined;
  const regionKind = parseRegionKind(searchParams.get("regionKind"));
  const regionId = searchParams.get("regionId") ?? undefined;
  const articleTypeParam = searchParams.get("articleType") ?? "";
  const articleType: ArticleType | undefined = isArticleType(articleTypeParam)
    ? articleTypeParam
    : undefined;

  const filters = useMemo(
    () => ({
      companyId,
      status,
      q,
      regionKind,
      regionId,
      articleType,
    }),
    [companyId, status, q, regionKind, regionId, articleType],
  );

  const listQuery = useQuery({
    queryKey: articleKeys.list(filters),
    queryFn: () => articlesService.list(filters),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => articlesService.remove(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: articleKeys.all });
      setDeleteTarget(null);
    },
  });

  function setArticleTypeParam(value: string) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set("articleType", value);
      else next.delete("articleType");
      return next;
    });
  }

  const rows = listQuery.data?.data ?? [];

  // Columns for the shared <DataTable>. Columns that don't map to a single
  // field (Regions, Actions) use a valid `keyof Article` accessor purely to
  // satisfy the type and supply a `render`. Cells default to nowrap; the Title
  // (free text) and Regions (chip list) columns opt into `wrap`.
  const columns: Column<Article>[] = [
    {
      header: "Firm",
      accessor: "companyId",
      render: (_value, row) => <FirmChip companyId={row.companyId} />,
    },
    {
      header: "Title",
      accessor: "title",
      wrap: true,
      render: (_value, row) => (
        <Link to={ROUTES.profiles.articleDetail(row.id)} className={styles.titleLink}>
          {row.title}
        </Link>
      ),
    },
    {
      header: "Type",
      accessor: "articleType",
      render: (_value, row) => ARTICLE_TYPE_LABELS[row.articleType],
    },
    {
      header: "Regions",
      accessor: "regions",
      wrap: true,
      render: (_value, row) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(row.regions ?? []).map(r => (
            <RegionNodeChip key={`${r.kind}:${r.id}`} region={r} />
          ))}
        </div>
      ),
    },
    {
      header: "Status",
      accessor: "status",
      render: (_value, row) => (
        <span className={styles.statusPill} data-status={row.status}>
          {row.status}
        </span>
      ),
    },
    {
      header: "Updated",
      accessor: "updatedAt",
      render: (_value, row) => (
        <span className={styles.muted}>{new Date(row.updatedAt).toLocaleDateString()}</span>
      ),
    },
    {
      header: "",
      accessor: "id",
      render: (_value, row) => (
        <div className={styles.actions}>
          <Button
            variant="outlined"
            size="small"
            aria-label={`View ${row.title}`}
            onClick={() => navigate(ROUTES.profiles.articleDetail(row.id))}
          >
            View
          </Button>
          <Button
            variant="outlined"
            size="small"
            aria-label={`Edit ${row.title}`}
            onClick={() => navigate(ROUTES.profiles.articleDetail(row.id))}
          >
            Edit
          </Button>
          <Button
            variant="text"
            color="error"
            size="small"
            aria-label={`Delete ${row.title}`}
            onClick={() => setDeleteTarget(row)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Articles">
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(ROUTES.profiles.articleNew)}
        >
          New article
        </Button>
      </PageHeader>

      <ProfilesListFilters enabledFilters={{ company: true, status: true, q: true, region: true }}>
        <ProfilesFilterCell>
          <Select
            label="Article type"
            value={articleTypeParam}
            options={ARTICLE_TYPE_OPTIONS}
            onChange={e => setArticleTypeParam(e.target.value)}
            aria-label="Article type filter"
            size="small"
          />
        </ProfilesFilterCell>
      </ProfilesListFilters>

      <div className={styles.contentSection}>
        {listQuery.isPending ? (
          <p role="status" className={styles.muted}>
            Loading…
          </p>
        ) : listQuery.isError ? (
          <p role="alert" className={styles.errorBanner}>
            {listQuery.error instanceof Error ? listQuery.error.message : "Failed to load"}
          </p>
        ) : rows.length === 0 ? (
          <p className={styles.emptyState}>No articles match the current filters.</p>
        ) : (
          <DataTable data={rows} columns={columns} rowKey="id" />
        )}
      </div>

      {deleteTarget && (
        <ConfirmationModal
          title="Delete article"
          message={`Delete article '${deleteTarget.title}'?`}
          warningMessage="This action cannot be undone."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await deleteMutation.mutateAsync(deleteTarget.id);
          }}
        />
      )}
    </div>
  );
}

export default ArticlesList;
